import { z } from "zod";

// Typed accessor for runtime config delivered by the BFF as /config.js.
// The BFF sets window.__SMYCLES_CONFIG__ before the main bundle loads
// (see apps/web/index.html). Reading the global here once and freezing
// gives the rest of the SPA an immutable, type-checked view.

const ConfigSchema = z.object({
  basePath: z.string().startsWith("/"),
  graphqlHttpPath: z.string().startsWith("/"),
  graphqlWsPath: z.string().startsWith("/"),
  keycloak: z.object({
    realm: z.string(),
    clientId: z.string(),
  }),
  tenantMode: z.string(),
  buildVersion: z.string(),
  featureFlags: z.record(z.string(), z.boolean()),
  // Defaulted so a config.js from a BFF that predates the Claude integration
  // (or a test fixture without the block) still parses — absent ⇒ disabled.
  mcp: z
    .object({
      enabled: z.boolean(),
      url: z.string(),
      clientId: z.string(),
      // MCP App names (widgets at `${mcpUrl()}/apps/<name>`). Defaulted for
      // the same reason as the block itself — absent ⇒ no app cards.
      apps: z.array(z.string()).default([]),
    })
    .default({ enabled: false, url: "", clientId: "claude", apps: [] }),
});

export type RuntimeConfig = z.infer<typeof ConfigSchema>;

declare global {
  // The BFF assigns this in /config.js. Treat as opaque until parsed.
  var __SMYCLES_CONFIG__: unknown;
}

let cached: RuntimeConfig | undefined;

export function getConfig(): RuntimeConfig {
  if (cached) return cached;
  const raw = globalThis.__SMYCLES_CONFIG__;
  const parsed = ConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(
      `Runtime config missing or malformed (window.__SMYCLES_CONFIG__). ` +
        `Issues: ${parsed.error.issues.map((i) => i.path.join(".")).join(", ")}. ` +
        `The BFF must serve /config.js before the SPA bundle loads, check that ` +
        `<script src="/config.js"> appears in index.html before the main script.`,
    );
  }
  cached = parsed.data;
  return cached;
}

// Helper that derives the absolute WS URL from current origin + path.
// Subscriptions open a ws:// (or wss://) connection; the path is what
// /config.js exposes.
export function graphqlWsUrl(): string {
  const cfg = getConfig();
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${cfg.graphqlWsPath}`;
}

// HTTP URL is relative - Apollo's HttpLink handles same-origin.
export function graphqlHttpUrl(): string {
  return getConfig().graphqlHttpPath;
}

// The MCP endpoint URL for the Claude integration. Explicit when configured
// (dev-compose, where the endpoint lives on another origin); otherwise derived
// from the current origin (cluster path-routing serves /mcp on the same host).
export function mcpUrl(): string {
  const cfg = getConfig();
  return cfg.mcp.url !== "" ? cfg.mcp.url : `${window.location.origin}/mcp`;
}
